import React, { ReactNode, useRef, useLayoutEffect, cloneElement, isValidElement, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useKeyboardAwareInput } from '@/hooks/useKeyboardAwareInput';

interface CompanionChatLayoutProps {
  header?: ReactNode;
  children: ReactNode;
  footer: ReactNode;
}

/**
 * Chat layout specifically designed for use within the Prayer Companion.
 * Uses companion-specific CSS classes instead of app-level classes to avoid conflicts.
 */
export default function CompanionChatLayout({ header, children, footer }: CompanionChatLayoutProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(112);
  
  const { 
    shouldShowOverlay, 
    handleInputFocus, 
    handleInputBlur, 
    keyboardState, 
    isMobile, 
    isDesktop,
    isIOS,
    keyboardHeight
  } = useKeyboardAwareInput();

  // Debug logging for layout
  React.useEffect(() => {
    if (isMobile) {
      console.log('[CompanionChatLayout] State update:', { 
        shouldShowOverlay, 
        keyboardState, 
        keyboardHeight,
        isMobile, 
        isIOS 
      });
      
      // Log footer transformation details
      if (shouldShowOverlay && keyboardHeight > 0) {
        console.log('[CompanionChatLayout] Footer transform:', `translateY(-${keyboardHeight}px)`);
      }
    }
  }, [shouldShowOverlay, keyboardState, keyboardHeight, isMobile, isIOS]);

  // Measure footer height dynamically
  useLayoutEffect(() => {
    const updateFooterHeight = () => {
      if (footerRef.current && containerRef.current) {
        const h = footerRef.current.offsetHeight;
        setFooterHeight(h);
        containerRef.current.style.setProperty('--companion-footer-height', `${h}px`);
      }
    };

    updateFooterHeight();
    
    const observer = new ResizeObserver(updateFooterHeight);
    if (footerRef.current) {
      observer.observe(footerRef.current);
    }
    
    window.addEventListener('resize', updateFooterHeight);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateFooterHeight);
    };
  }, [footer]);

  // Auto-scroll whenever children change
  useLayoutEffect(() => {
    const vp = viewportRef.current;
    if (vp) {
      requestAnimationFrame(() => {
        vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [children]);

  // Handle overlay click to dismiss keyboard
  const handleOverlayClick = () => {
    if (isMobile) {
      console.log('[CompanionChatLayout] Overlay clicked - dismissing keyboard');
    }
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && activeElement.blur) {
      activeElement.blur();
    }
  };

  // Clone footer to inject keyboard handlers if it's a React element
  const enhancedFooter = React.useMemo(() => {
    if (isValidElement(footer)) {
      return cloneElement(footer, {
        onInputFocus: handleInputFocus,
        onInputBlur: handleInputBlur,
        ...footer.props
      });
    }
    return footer;
  }, [footer, handleInputFocus, handleInputBlur]);

  return (
    <div className="companion-chat-container" ref={containerRef}>
      {header && <div className="flex-shrink-0">{header}</div>}

      {/* Scrollable chat content with dynamic padding for footer */}
      <div className="companion-chat-content">
        <ScrollArea 
          ref={viewportRef}
          className="absolute inset-0"
          viewportClassName="companion-scroll-viewport p-4 space-y-4 flex flex-col"
        >
          {children}
        </ScrollArea>
      </div>

      {/* Overlay for keyboard active state */}
      <div 
        className={`companion-input-overlay ${shouldShowOverlay ? 'active' : ''}`}
        onClick={handleOverlayClick}
      />

      {/* Footer with responsive positioning */}
      {enhancedFooter && (
        <div 
          ref={footerRef}
          className={`companion-chat-footer ${isDesktop ? 'companion-chat-footer-desktop' : 'companion-chat-footer-mobile'}`}
          style={isDesktop ? {
            paddingBottom: `calc(1rem + env(safe-area-inset-bottom, 0px))`
          } : {
            transform: shouldShowOverlay && keyboardHeight > 0 
              ? `translateY(-${keyboardHeight}px)` 
              : 'translateY(0)',
            paddingBottom: `calc(1rem + env(safe-area-inset-bottom, 0px))`
          }}
        >
          {enhancedFooter}
        </div>
      )}
    </div>
  );
}